/**
 * 文件路径: D:/XXY/MeiShan_Project/frontend/shared/api/dishTags.ts
 * 版本: v1.0
 * 创建日期: 2026-03-05
 * 创建人: AI Assistant
 *
 * 菜品标签 API 封装
 *
 * 功能：
 * - 菜品标签列表查询（支持筛选、搜索、分页）
 * - 菜品标签创建
 * - 菜品标签详情查询
 * - 菜品标签更新
 * - 菜品标签删除
 *
 * 依赖：
 * - httpClient: HTTP 请求客户端
 * - PRESET_TAG_COLORS / tagColorUtils: 复用菜单标签的预设颜色与工具
 */

import { httpClient } from './http'

// 复用菜单标签的预设颜色和工具（统一色板）
export { PRESET_TAG_COLORS, tagColorUtils } from './menuTags'

/**
 * 菜品标签（ReadDTO）
 */
export interface DishTag {
  id: string | number
  name: string
  color: string
  description: string | null
  operator_tenant: number | string | null
  operator_tenant_name: string | null
  merchant: number | string | null
  merchant_name: string | null
  owner_display: string
  dish_count: number
  created_by: number | string | null
  created_by_name: string | null
  created_at: string
  updated_at: string
}

/**
 * 菜品标签创建/更新请求类型
 * 注意：operator_tenant 由后端根据当前用户自动注入，前端无需传递
 * merchant：运营商管理员可指定商户（创建商户专属标签），留空为全局标签；
 *           商户管理员由后端自动覆盖为当前商户，前端无需传递
 */
export interface DishTagRequest {
  name: string
  color?: string
  description?: string | null
  /** 所属商户ID（运营商端可选，留空为全局标签；商户端由后端自动注入） */
  merchant?: number | string | null
}

/**
 * 菜品标签列表查询参数
 */
export interface DishTagListParams {
  /** 页码（1-based） */
  page?: number
  /** 每页数量 */
  page_size?: number
  /** 商户ID（过滤商户专属标签） */
  merchant?: string | number
  /** 搜索关键词（标签名称、描述） */
  search?: string
  /** 排序字段 */
  ordering?: string
}

/**
 * 菜品标签分页响应
 */
export interface DishTagListResponse {
  count: number
  next: string | null
  previous: string | null
  results: DishTag[]
}

/**
 * 菜品标签 API
 */
export const dishTagsApi = {
  /**
   * 获取菜品标签列表
   *
   * @param params 查询参数
   * @returns 菜品标签分页列表
   */
  async list(params?: DishTagListParams): Promise<DishTagListResponse> {
    const response = await httpClient.get<DishTagListResponse>(
      'merchants/dish-tags/',
      { params }
    )
    return response
  },

  /**
   * 获取菜品标签详情
   *
   * @param id 标签ID
   * @returns 菜品标签详情
   */
  async get(id: string | number): Promise<DishTag> {
    const response = await httpClient.get<DishTag>(
      `merchants/dish-tags/${id}/`
    )
    return response
  },

  /**
   * 创建菜品标签
   *
   * @param data 标签数据
   * @returns 创建的标签
   */
  async create(data: DishTagRequest): Promise<DishTag> {
    const response = await httpClient.post<DishTag>(
      'merchants/dish-tags/',
      data
    )
    return response
  },

  /**
   * 更新菜品标签
   *
   * @param id 标签ID
   * @param data 更新数据（部分字段）
   * @returns 更新后的标签
   */
  async update(id: string | number, data: Partial<DishTagRequest>): Promise<DishTag> {
    const response = await httpClient.patch<DishTag>(
      `merchants/dish-tags/${id}/`,
      data
    )
    return response
  },

  /**
   * 删除菜品标签
   *
   * @param id 标签ID
   */
  async delete(id: string | number): Promise<void> {
    await httpClient.delete(`merchants/dish-tags/${id}/`)
  },

  /**
   * 批量获取菜品标签（不分页）
   *
   * @param params 查询参数
   * @returns 所有匹配的标签
   */
  async all(params?: Omit<DishTagListParams, 'page' | 'page_size'>): Promise<DishTag[]> {
    const response = await httpClient.get<DishTagListResponse>(
      'merchants/dish-tags/',
      {
        params: {
          ...params,
          page_size: 1000
        }
      }
    )
    return response.results
  }
}
